/**
 * Lenavio Org — interactive org chart.
 *
 * Visual horizontal tree with boxes connected by SVG lines, plus
 * pan / zoom / search / collapse controls. No third-party chart
 * library — the tree is laid out by a small recursive algorithm
 * and painted with absolutely-positioned divs + SVG connectors.
 *
 * Why hand-rolled:
 *   * react-flow / d3-org-chart pull in 200-500 KB of JS for what
 *     amounts to a couple of hundred boxes for most orgs. The
 *     custom layout below is ~150 lines of code that ships in
 *     the existing bundle without a new dependency.
 *
 * Pan: mousedown on the canvas, drag, mouseup.
 * Zoom: wheel events + buttons (range 0.4x – 2x).
 * Search: filters the input → finds first match by name → centers
 *         + highlights the node.
 * Collapse: click a node's "▾/▸" pill to hide its subtree (state
 *           is keyed by id).
 *
 * Profile preview pops the existing DirectoryProfilePanel side-
 * sheet on click — same panel as Directory + Talent Map.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { motion } from "framer-motion";
import {
  ChevronDown,
  ChevronRight,
  Maximize2,
  Minus,
  Plus,
  Search,
  X,
} from "lucide-react";

import { getOrgChartTree, type OrgChartNode } from "@/api/employees";
import { Avatar } from "@/components/ui/Avatar";
import { Card } from "@/components/ui/Card";
import { DirectoryProfilePanel } from "@/components/directory/DirectoryProfilePanel";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/hooks/useToast";
import { pageTransition } from "@/lib/utils";

// ── Layout constants ────────────────────────────────────────────────
//
// All in viewport pixels; the tree applies CSS transform: scale()
// for zoom so the underlying coordinate system stays simple.

const NODE_WIDTH = 220;
const NODE_HEIGHT = 76;
const VERTICAL_GAP = 60; // between parent row and children row
const HORIZONTAL_GAP = 24; // between sibling subtrees

const ZOOM_MIN = 0.4;
const ZOOM_MAX = 2.0;
const ZOOM_STEP = 0.15;


// ── Layout types ────────────────────────────────────────────────────

interface LaidOutNode {
  node: OrgChartNode;
  x: number; // centre X of this node in canvas coordinates
  y: number; // top-left Y
  subtreeWidth: number; // width consumed by this node + descendants
  children: LaidOutNode[];
}


/**
 * Recursive layout — bottom-up width pass, then top-down position pass.
 *
 * Subtree width = max(node width, sum(child subtree widths) + gaps).
 * Each child's centre is placed sequentially using the running
 * cursor; the parent then centres itself over its children.
 */
function layoutTree(
  node: OrgChartNode,
  collapsed: ReadonlySet<string>,
  startY = 0,
): LaidOutNode {
  const isCollapsed = collapsed.has(node.id);
  const renderChildren = isCollapsed ? [] : node.direct_reports;

  const children = renderChildren.map((c) =>
    layoutTree(c, collapsed, startY + NODE_HEIGHT + VERTICAL_GAP),
  );

  const childrenWidth = children.length
    ? children.reduce((sum, c) => sum + c.subtreeWidth, 0) +
      HORIZONTAL_GAP * (children.length - 1)
    : 0;
  const subtreeWidth = Math.max(NODE_WIDTH, childrenWidth);

  // Start placing children from the left edge (relative to 0); the
  // caller offsets the entire subtree afterward.
  let cursor = 0;
  const positionedChildren: LaidOutNode[] = [];
  for (const c of children) {
    const cWidth = c.subtreeWidth;
    // Centre this child within its slot.
    const childCentreX = cursor + cWidth / 2;
    // Recursively shift this child's subtree by the cursor delta.
    shiftSubtree(c, childCentreX - c.x);
    positionedChildren.push(c);
    cursor += cWidth + HORIZONTAL_GAP;
  }

  // Centre this node over its children block.
  const myCentreX =
    children.length > 0 ? cursor === 0 ? 0 : (cursor - HORIZONTAL_GAP) / 2 : NODE_WIDTH / 2;

  return {
    node,
    x: myCentreX,
    y: startY,
    subtreeWidth,
    children: positionedChildren,
  };
}


function shiftSubtree(n: LaidOutNode, dx: number): void {
  n.x += dx;
  for (const c of n.children) shiftSubtree(c, dx);
}


/**
 * Produce a flat list of (node, x, y) for rendering, plus the
 * canvas bounding box. The recursion above lays out a single root
 * starting at x = whatever-centre — we shift it so the leftmost
 * node sits at x=0 with a margin.
 */
function flatten(roots: LaidOutNode[]): {
  nodes: LaidOutNode[];
  edges: { fromX: number; fromY: number; toX: number; toY: number }[];
  width: number;
  height: number;
} {
  // Place roots side-by-side.
  let cursor = 0;
  for (const r of roots) {
    shiftSubtree(r, cursor - (r.x - r.subtreeWidth / 2));
    cursor += r.subtreeWidth + HORIZONTAL_GAP * 2;
  }

  const nodes: LaidOutNode[] = [];
  const edges: { fromX: number; fromY: number; toX: number; toY: number }[] =
    [];
  let maxX = 0;
  let maxY = 0;
  const walk = (n: LaidOutNode) => {
    nodes.push(n);
    const px = n.x;
    const py = n.y;
    maxX = Math.max(maxX, px + NODE_WIDTH / 2);
    maxY = Math.max(maxY, py + NODE_HEIGHT);
    for (const c of n.children) {
      edges.push({
        fromX: px,
        fromY: py + NODE_HEIGHT,
        toX: c.x,
        toY: c.y,
      });
      walk(c);
    }
  };
  for (const r of roots) walk(r);

  // Symmetric padding around the canvas.
  const pad = 40;
  for (const n of nodes) {
    n.x += pad;
    n.y += pad;
  }
  for (const e of edges) {
    e.fromX += pad;
    e.toX += pad;
    e.fromY += pad;
    e.toY += pad;
  }
  return {
    nodes,
    edges,
    width: maxX + pad * 2,
    height: maxY + pad * 2,
  };
}


// ── Page component ──────────────────────────────────────────────────

export function OrgChartPage() {
  const { addToast } = useToast();
  const [roots, setRoots] = useState<OrgChartNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(
    null,
  );
  const canvasRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    getOrgChartTree()
      .then((rs) => {
        if (!cancelled) setRoots(rs);
      })
      .catch(() => addToast("error", "Failed to load org chart"))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [addToast]);

  // Compute layout. Re-runs on data change OR collapse toggle.
  const { nodes, edges, width, height, totalCount } = useMemo(() => {
    const positioned = roots.map((r) => layoutTree(r, collapsed));
    const flat = flatten(positioned);
    let count = 0;
    const tally = (n: OrgChartNode) => {
      count += 1;
      n.direct_reports.forEach(tally);
    };
    roots.forEach(tally);
    return { ...flat, totalCount: count };
  }, [roots, collapsed]);

  // Search match — case-insensitive on full name + title.
  const lowerQ = search.trim().toLowerCase();
  const matchedIds = useMemo(() => {
    if (!lowerQ) return new Set<string>();
    const out = new Set<string>();
    for (const n of nodes) {
      const hay = `${n.node.name} ${n.node.title}`.toLowerCase();
      if (hay.includes(lowerQ)) out.add(n.node.id);
    }
    return out;
  }, [nodes, lowerQ]);

  // Centre on the first match whenever the query changes.
  useEffect(() => {
    if (!lowerQ) return;
    const first = nodes.find((n) => matchedIds.has(n.node.id));
    if (!first || !canvasRef.current) return;
    const el = canvasRef.current;
    const cw = el.clientWidth;
    const ch = el.clientHeight;
    setPan({
      x: cw / 2 - first.x * zoom,
      y: ch / 2 - first.y * zoom,
    });
    // Don't re-pan as the user toggles other things — only on
    // query changes. Run once per `lowerQ` change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lowerQ]);

  const toggle = useCallback((id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const fit = useCallback(() => {
    if (!canvasRef.current) return;
    const el = canvasRef.current;
    const cw = el.clientWidth;
    const ch = el.clientHeight;
    if (width <= 0 || height <= 0) return;
    const z = Math.min(cw / width, ch / height, 1);
    setZoom(z);
    setPan({ x: (cw - width * z) / 2, y: (ch - height * z) / 2 });
  }, [width, height]);

  // Initial fit once data lands.
  useEffect(() => {
    if (!loading && roots.length > 0) {
      // Small delay so the canvas size is final after layout.
      requestAnimationFrame(fit);
    }
  }, [loading, roots, fit]);

  const onWheel = (e: React.WheelEvent) => {
    if (!e.ctrlKey && !e.metaKey) return; // require modifier so vertical
    // page scroll still works in tall lists
    e.preventDefault();
    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    setZoom((z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z + delta)));
  };

  const onMouseDown = (e: React.MouseEvent) => {
    // Only start a drag on left button + non-button targets.
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest("[data-org-node]") || target.closest("button")) return;
    dragRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragRef.current) return;
    setPan({
      x: dragRef.current.panX + (e.clientX - dragRef.current.x),
      y: dragRef.current.panY + (e.clientY - dragRef.current.y),
    });
  };
  const stopDrag = () => {
    dragRef.current = null;
  };

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Org chart"
        subtitle={
          loading
            ? "Loading…"
            : `${totalCount} people across ${roots.length} top-level report${
                roots.length === 1 ? "" : "s"
              }`
        }
      />

      <div className="flex gap-3 mb-3 items-center flex-wrap">
        <div className="relative flex-1 min-w-[12rem]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or title…"
            className="w-full pl-9 pr-9 py-2 rounded-lg border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() =>
              setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP))
            }
            className="p-2 rounded-lg border border-border bg-surface hover:bg-background"
            aria-label="Zoom out"
            title="Zoom out"
          >
            <Minus className="h-4 w-4" />
          </button>
          <span className="text-xs text-text-muted min-w-[3rem] text-center">
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            onClick={() =>
              setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP))
            }
            className="p-2 rounded-lg border border-border bg-surface hover:bg-background"
            aria-label="Zoom in"
            title="Zoom in"
          >
            <Plus className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={fit}
            className="p-2 rounded-lg border border-border bg-surface hover:bg-background"
            aria-label="Fit to screen"
            title="Fit to screen"
          >
            <Maximize2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <Card padding={false} className="overflow-hidden">
        <div
          ref={canvasRef}
          className="relative w-full h-[70vh] cursor-grab active:cursor-grabbing select-none bg-background"
          onWheel={onWheel}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={stopDrag}
          onMouseLeave={stopDrag}
        >
          {loading ? (
            <div className="h-full flex items-center justify-center text-text-muted text-sm">
              Loading…
            </div>
          ) : nodes.length === 0 ? (
            <div className="h-full flex items-center justify-center text-text-muted text-sm">
              No employees yet.
            </div>
          ) : (
            <div
              className="absolute top-0 left-0"
              style={{
                width,
                height,
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transformOrigin: "0 0",
              }}
            >
              {/* Connectors layer */}
              <svg
                className="absolute top-0 left-0 pointer-events-none"
                width={width}
                height={height}
                aria-hidden
              >
                {edges.map((e, i) => {
                  // Smooth bezier between parent bottom-centre and
                  // child top-centre. Mid-y at half the vertical gap
                  // gives a clean "S" if the children spread sideways.
                  const midY = (e.fromY + e.toY) / 2;
                  return (
                    <path
                      key={i}
                      d={`M ${e.fromX} ${e.fromY} C ${e.fromX} ${midY}, ${e.toX} ${midY}, ${e.toX} ${e.toY}`}
                      fill="none"
                      stroke="rgb(var(--color-border))"
                      strokeWidth={1.5}
                    />
                  );
                })}
              </svg>

              {/* Node boxes */}
              {nodes.map((n) => {
                const dimmed = lowerQ.length > 0 && !matchedIds.has(n.node.id);
                const matched = matchedIds.has(n.node.id);
                const isCollapsed = collapsed.has(n.node.id);
                const hasChildren = n.node.direct_reports.length > 0;
                return (
                  <div
                    key={n.node.id}
                    data-org-node
                    className="absolute"
                    style={{
                      left: n.x - NODE_WIDTH / 2,
                      top: n.y,
                      width: NODE_WIDTH,
                      height: NODE_HEIGHT,
                    }}
                  >
                    <div
                      className={`relative h-full rounded-xl border bg-surface px-3 py-2 transition-shadow ${
                        matched
                          ? "border-accent shadow-md"
                          : dimmed
                            ? "border-border/40 opacity-50"
                            : "border-border hover:border-accent/50 hover:shadow-sm"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => setSelected(n.node.id)}
                        className="flex items-center gap-2 w-full text-left h-full"
                        aria-label={`Open ${n.node.name}'s profile`}
                      >
                        <Avatar
                          firstName={n.node.first_name}
                          lastName={n.node.last_name}
                          src={n.node.avatar_url}
                          size="sm"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-text-primary truncate">
                            {n.node.name}
                          </p>
                          <p className="text-xs text-text-muted truncate">
                            {n.node.title || "—"}
                          </p>
                          {n.node.department && (
                            <p className="text-[11px] text-text-muted truncate">
                              {n.node.department}
                            </p>
                          )}
                        </div>
                      </button>
                      {hasChildren && (
                        <button
                          type="button"
                          onClick={() => toggle(n.node.id)}
                          aria-label={
                            isCollapsed
                              ? `Expand ${n.node.first_name}'s reports`
                              : `Collapse ${n.node.first_name}'s reports`
                          }
                          className="absolute -bottom-2 left-1/2 -translate-x-1/2 rounded-full border border-border bg-surface w-6 h-6 flex items-center justify-center text-text-muted hover:text-accent hover:border-accent shadow-sm"
                          title={`${n.node.direct_reports.length} report${
                            n.node.direct_reports.length === 1 ? "" : "s"
                          }`}
                        >
                          {isCollapsed ? (
                            <ChevronRight className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronDown className="h-3.5 w-3.5" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="absolute bottom-3 right-3 text-[11px] text-text-muted bg-surface/80 backdrop-blur px-2 py-1 rounded">
            Drag to pan · ⌘/Ctrl + scroll to zoom
          </div>
        </div>
      </Card>

      <DirectoryProfilePanel
        employeeId={selected}
        onClose={() => setSelected(null)}
      />
    </motion.div>
  );
}
